import { useRef, useState } from 'react';
import { Combobox } from '@base-ui/react/combobox';
import { Search } from 'lucide-react';
import type { SupportedSymbol } from '@/lib/symbols';
import type { Ticker } from '@/store/heatmapStore';
import { DropdownChevron } from './icons/DropdownChevron';

interface TickerSelectorProps {
  symbols: readonly SupportedSymbol[];
  activeTicker: Ticker;
  spotPrice: number | null;
  onTickerChange: (ticker: Ticker) => void;
}

export function TickerSelector({
  symbols,
  activeTicker,
  spotPrice,
  onTickerChange,
}: TickerSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);

  return (
    <Combobox.Root
      items={symbols}
      value={symbols.find((symbol) => symbol.ticker === activeTicker) ?? null}
      inputValue={query}
      open={isOpen}
      autoHighlight
      itemToStringLabel={(symbol) => symbol.ticker}
      itemToStringValue={(symbol) => symbol.ticker}
      isItemEqualToValue={(symbol, value) => symbol.ticker === value.ticker}
      filter={(symbol, inputValue) => symbol.ticker.includes(inputValue.trim().toUpperCase())}
      onInputValueChange={setQuery}
      onOpenChange={(open) => {
        setIsOpen(open);
        if (!open) {
          setQuery('');
        }
      }}
      onValueChange={(symbol) => {
        if (symbol != null) {
          onTickerChange(symbol.ticker);
          setIsOpen(false);
          setQuery('');
        }
      }}
      disabled={symbols.length === 0}
    >
      <Combobox.Trigger
        aria-label={`Select ticker ${activeTicker}`}
        aria-busy={symbols.length === 0}
        className="flex h-10 w-full min-w-0 items-center justify-between gap-3 rounded-md border border-line-strong bg-panel px-3 font-mono text-[13px] tracking-[0.04em] text-fg hover:bg-panel-2 disabled:cursor-wait disabled:text-subtle"
      >
        <span className="flex min-w-0 items-center gap-[9px]">
          <span>{activeTicker}</span>
          {spotPrice != null && (
            <>
              <span className="h-[7px] w-[7px] shrink-0 rounded-full bg-up shadow-[0_0_6px_rgba(34,197,94,0.5)]" />
              <span className="truncate text-xs text-subtle">${spotPrice.toFixed(2)}</span>
            </>
          )}
        </span>
        <DropdownChevron />
      </Combobox.Trigger>

      <Combobox.Portal>
        <Combobox.Positioner
          sideOffset={6}
          align="start"
          className="z-[70] w-[max(250px,var(--anchor-width))]"
        >
          <Combobox.Popup
            initialFocus={searchInputRef}
            aria-label="Ticker options"
            className="w-full rounded-md border border-line-strong bg-panel-2 p-1 leading-[normal] shadow-[0_14px_34px_rgba(0,0,0,0.55)]"
          >
            <div className="mb-1 flex items-center gap-2 border-b border-line px-2 pb-2 pt-1.5">
              <Search className="h-[13px] w-[13px] text-dim" strokeWidth={2} />
              <Combobox.Input
                ref={searchInputRef}
                aria-label="Search ticker"
                placeholder="Search ticker"
                className="min-w-0 flex-1 bg-transparent font-mono text-[12.5px] text-fg outline-none placeholder:text-dim"
              />
            </div>
            <Combobox.List className="max-h-[280px] overflow-y-auto">
              {(symbol: SupportedSymbol, index: number) => {
                const isActive = symbol.ticker === activeTicker;
                return (
                  <Combobox.Item
                    key={symbol.ticker}
                    value={symbol}
                    index={index}
                    nativeButton
                    render={<button type="button" />}
                    tabIndex={-1}
                    onPointerUp={(event) => {
                      if (event.pointerType === 'touch') {
                        event.preventDefault();
                        event.currentTarget.click();
                      }
                    }}
                    className={`flex w-full cursor-default items-center justify-between gap-2.5 rounded px-2.5 py-[9px] text-left font-mono text-[12.5px] data-[highlighted]:bg-white/[.05] data-[highlighted]:text-fg ${
                      isActive
                        ? 'bg-white/[.08] text-fg'
                        : 'text-subtle hover:bg-white/[.05] hover:text-fg'
                    }`}
                  >
                    <span>{symbol.ticker}</span>
                  </Combobox.Item>
                );
              }}
            </Combobox.List>
            <Combobox.Empty>
              <div className="px-2.5 pb-1 pt-2 font-mono text-[10.5px] text-dim">
                No symbols found
              </div>
            </Combobox.Empty>
          </Combobox.Popup>
        </Combobox.Positioner>
      </Combobox.Portal>
    </Combobox.Root>
  );
}
